/**
 * API Client del Promotor Digital.
 *
 * Reusa el flujo del cashier (login de caja + x-admin-token), recortado a lo
 * que el panel del promotor necesita: login, canje del link de acceso web,
 * GET config, y los 2 GET del relatorio. SOLO LECTURA del lado del promotor.
 */

import PromoterConfig from './config.js';
import { extraerTokenAcceso } from '@shared/config/acceso-corto.js';

const { API_URL, STORAGE_KEYS } = PromoterConfig;

// Sesión en localStorage (no sessionStorage): persiste al cerrar la app/pestaña,
// así el promotor con su device anclado reentra SIN teclear nada (isLoggedIn →
// panel directo). Mismo comportamiento en web, Android e iOS instalado/sin
// instalar. El backend revoca la sesión si el admin regenera el link.
let _token = localStorage.getItem(STORAGE_KEYS.TOKEN);
let _user = null;
try {
    const stored = localStorage.getItem(STORAGE_KEYS.USER);
    if (stored) _user = JSON.parse(stored);
} catch (_) {
    /* JSON corrupto: ignorar */
}

let _onSessionExpired = null;
function setOnSessionExpired(cb) {
    _onSessionExpired = cb;
}

// ── Canje del link de acceso web (?acceso=<token>) ──────────────────────
// El admin, al crear el Promotor Digital, generó un token de un solo uso. El
// promotor abre su app con ?acceso=<token> y acá lo canjeamos por el
// device-token persistente. Idéntico al canje de caja web del cashier.

function _getFingerprint() {
    let fp = localStorage.getItem(STORAGE_KEYS.WEB_FINGERPRINT);
    if (!fp) {
        // Fingerprint estable simple (no anti-fraude, solo idempotencia del canje).
        fp = 'fp-' + Math.abs(_hashStr(navigator.userAgent + screen.width + screen.height)).toString(36);
        localStorage.setItem(STORAGE_KEYS.WEB_FINGERPRINT, fp);
    }
    return fp;
}
function _hashStr(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = (h << 5) - h + s.charCodeAt(i);
        h |= 0;
    }
    return h;
}

// Limpia el código de la URL tras canjearlo: borra el ?acceso= legacy del query
// Y el código corto del path (deja la raíz "/"), para que un reload no reintente
// un token ya quemado ni quede el código a la vista.
function _limpiarAccesoDeLaUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        params.delete('acceso');
        const query = params.toString();
        window.history.replaceState({}, document.title, '/' + (query ? '?' + query : '') + window.location.hash);
    } catch (_) {
        /* history puede fallar en contextos raros */
    }
}

// Lee el código de acceso de la URL: del PATH (partner.<brand>/481902, formato
// nuevo humano) o del query (?acceso=<hex64>, legacy). Devuelve el string crudo o
// null. La extracción/validación de formato la hace extraerTokenAcceso (SSOT).
function _leerCodigoDeLaUrl() {
    const params = new URLSearchParams(window.location.search);
    const legacy = params.get('acceso');
    if (legacy) return legacy;
    // Path: todo lo que sigue a la raíz (sin el "/" inicial). El extractor del
    // SSOT decide si es un código válido; acá solo lo pasamos crudo.
    const path = window.location.pathname.replace(/^\/+/, '');
    return path || null;
}

// Canjea un token de acceso (código corto o hex64 legacy) contra el backend.
// Es el SSOT del canje: lo usa tanto el boot (código en la URL) como la caja de
// pegar de la app instalada. Devuelve true si quedó un dispositivoToken usable.
// `limpiarUrl` solo aplica cuando el token vino de la URL.
async function canjearTokenAcceso(tokenCrudo, { limpiarUrl = false } = {}) {
    const extraido = extraerTokenAcceso(tokenCrudo);
    if (!extraido) {
        if (limpiarUrl) _limpiarAccesoDeLaUrl();
        return false;
    }
    try {
        const resp = await fetch(`${API_URL}/api/cashier/web/canjear-acceso`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accesoToken: extraido.valor, fingerprint: _getFingerprint() }),
        });
        const data = await resp.json();
        if (resp.ok && data.ok && data.dispositivoToken && data.serial) {
            localStorage.setItem(STORAGE_KEYS.SERIAL, data.serial);
            localStorage.setItem(STORAGE_KEYS.DISPOSITIVO_TOKEN, data.dispositivoToken);
            if (limpiarUrl) _limpiarAccesoDeLaUrl();
            return true;
        }
        // Rechazo definitivo del backend: el token no sirve más.
        if (limpiarUrl) _limpiarAccesoDeLaUrl();
        return false;
    } catch (_) {
        // Error de red: NO limpiar la URL (un reload reintenta, fingerprint estable).
        return false;
    }
}

// Devuelve el token VÁLIDO presente en la URL SIN canjearlo (ni tocar el backend
// ni borrar la URL), o null. El boot lo usa para decidir si mostrar la bienvenida
// ANTES de canjear: así el código no se quema en el navegador si el promotor va a
// instalar (en iOS el canje en Safari dejaría inutilizable la app instalada).
function leerTokenValidoDeLaUrl() {
    const crudo = _leerCodigoDeLaUrl();
    if (!crudo) return null;
    const extraido = extraerTokenAcceso(crudo);
    return extraido ? extraido.valor : null;
}

// Email del último login exitoso (para pre-rellenar el campo al reentrar).
function getUltimoEmail() {
    return localStorage.getItem(STORAGE_KEYS.ULTIMO_EMAIL) || '';
}

function hasDispositivoToken() {
    return !!localStorage.getItem(STORAGE_KEYS.DISPOSITIVO_TOKEN);
}

// ── Activación de cuenta (onboarding universal) ──────────────────────────
// El promotor nace SIN contraseña y la crea en su primer acceso. Estos
// endpoints son públicos: el dispositivoToken (obtenido al canjear el link)
// ES la autorización — el promotor aún no puede loguear.

async function _postActivacion(endpoint, extraBody) {
    const dispositivoToken = localStorage.getItem(STORAGE_KEYS.DISPOSITIVO_TOKEN);
    if (!dispositivoToken) return { ok: false, error: 'SIN_DISPOSITIVO' };
    let response;
    try {
        response = await fetch(`${API_URL}${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dispositivoToken, ...extraBody }),
        });
    } catch (_) {
        return { ok: false, error: 'NETWORK_ERROR', mensaje: 'Sin conexión al servidor' };
    }
    let data;
    try {
        data = await response.json();
    } catch (_) {
        return { ok: false, error: 'PARSE_ERROR', mensaje: 'Respuesta inválida del servidor' };
    }
    if (!response.ok) {
        return {
            ok: false,
            error: (data && data.code) || 'HTTP_ERROR',
            mensaje: (data && (data.error || data.mensaje)) || 'Error del servidor',
            httpStatus: response.status,
        };
    }
    return data;
}

// ¿Esta caja tiene una cuenta pendiente de activación? → { requiereActivacion,
// tieneCorreo, correoMasked, nombre } | { multiplesPendientes: true } (2+ cuentas:
// re-consultar pasando el correo del que se activa). El boot decide la pantalla.
function activacionEstado(correo) {
    return _postActivacion('/api/cashier/activacion-web/estado', correo ? { correo } : {});
}

// Variante autogestión: pedir el código al correo que el promotor declaró.
function activacionSolicitarCodigo(correo) {
    return _postActivacion('/api/cashier/activacion-web/solicitar-codigo', { correo });
}

// Crear la contraseña. `correo`: en autogestión va con `codigo` (verificación);
// en multi-pendiente va solo (selecciona la cuenta de la variante b).
async function activacionCrearContrasena({ password, passwordRepetida, correo, codigo }) {
    const r = await _postActivacion('/api/cashier/activacion-web/crear-contrasena', {
        password,
        passwordRepetida,
        ...(correo ? { correo, ...(codigo ? { codigo } : {}) } : {}),
    });
    // El backend ya dejó la sesión iniciada (token): persistirla y entrar directo
    // al panel — el promotor acaba de definir correo+seña, no los re-teclea. El
    // _user se hidrata del config al entrar; acá basta el token. Si no vino token
    // (fallback), el front cae al login con el email recordado.
    if (r && r.ok && r.token) {
        _persistirSesion(r.token, null, r.email);
    } else if (r && r.ok && r.email) {
        localStorage.setItem(STORAGE_KEYS.ULTIMO_EMAIL, r.email);
    }
    return r;
}

// Cambiar la contraseña (sesión activa): re-auth con la actual + la nueva doble.
function cambiarContrasena({ passwordActual, password, passwordRepetida }) {
    return post('/api/cashier/cambiar-contrasena', { passwordActual, password, passwordRepetida });
}

// "Olvidé mi contraseña" (sin sesión): el código va al correo YA registrado del
// usuario de esta caja — el backend lo resuelve por dispositivoToken (anti-takeover).
// `correo` opcional: con 2+ cuentas en la caja (turnos) SELECCIONA cuál — el código
// igual va al correo registrado de esa cuenta.
function resetSolicitarCodigo(correo) {
    return _postActivacion('/api/cashier/activacion-web/solicitar-codigo-reset', correo ? { correo } : {});
}

function resetRestablecerContrasena({ codigo, password, passwordRepetida, correo }) {
    return _postActivacion('/api/cashier/activacion-web/restablecer-contrasena', {
        codigo,
        password,
        passwordRepetida,
        ...(correo ? { correo } : {}),
    });
}

// Cambiar el correo (sesión activa): re-auth con password + código al correo NUEVO.
// Reusa los endpoints del cajero que ya están en producción.
function correoSolicitarCodigo(correo) {
    return post('/api/cashier/correo/solicitar-codigo', { correo });
}

async function correoVerificarCodigo({ codigo, password }) {
    const r = await post('/api/cashier/correo/verificar-codigo', { codigo, password });
    // Si el cambio fue OK, actualizar el email recordado para que el prefill del
    // login muestre el NUEVO correo, no el anterior.
    if (r && r.ok && r.correo) {
        localStorage.setItem(STORAGE_KEYS.ULTIMO_EMAIL, r.correo);
    }
    return r;
}

// ── Login ────────────────────────────────────────────────────────────────

// Persiste la sesión iniciada (token + usuario en localStorage para que sobreviva
// el cierre de la app) y recuerda el email (fallback de prefill si la sesión
// expira). SSOT del "quedar logueado" — usado por login() y por la activación.
function _persistirSesion(token, usuario, email) {
    _token = token;
    _user = usuario;
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(usuario));
    if (email) localStorage.setItem(STORAGE_KEYS.ULTIMO_EMAIL, email);
}

async function login(email, password) {
    const dispositivoToken = localStorage.getItem(STORAGE_KEYS.DISPOSITIVO_TOKEN);
    let response;
    try {
        response = await fetch(`${API_URL}/api/cashier/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, dispositivoToken }),
        });
    } catch (_) {
        return { ok: false, error: 'Sin conexión al servidor' };
    }
    const data = await response.json();
    if (!response.ok || !data.ok) {
        // code viaja al caller: 'cuenta_sin_contrasena' (409) deriva al flujo de
        // activación en vez de mostrar "credenciales inválidas".
        return { ok: false, error: data.error || 'Error de login', code: data.code || null };
    }
    _persistirSesion(data.token, data.usuario, email);
    return { ok: true, usuario: _user, dispositivo: data.dispositivo };
}

function logout() {
    _token = null;
    _user = null;
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
}

// Maneja un 401. El backend distingue por `code`:
//   'device_revoked' → el admin regeneró el link / revocó el dispositivo. El
//                      device-token de localStorage YA NO SIRVE: limpiarlo para que
//                      el front muestre "pedí un enlace nuevo" (no la pantalla login).
//   resto            → expiración normal de sesión: conservar el device-token (el
//                      promotor puede re-loguear con el mismo dispositivo).
// Recibe el body ya parseado (el code viene en el JSON). Devuelve el error al caller.
function _manejarRechazoAuth(data) {
    const esRevocado = data && data.code === 'device_revoked';
    logout();
    if (esRevocado) {
        localStorage.removeItem(STORAGE_KEYS.DISPOSITIVO_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.SERIAL);
    }
    const motivo = esRevocado ? 'device_revoked' : 'session_expired';
    if (_onSessionExpired) _onSessionExpired(motivo);
    return { ok: false, error: 'SESSION_EXPIRED', code: motivo };
}

function isLoggedIn() {
    return !!_token;
}
function getUser() {
    return _user;
}

// ── HTTP GET (lo único que el panel necesita; es read-only) ─────────────
async function get(endpoint) {
    const headers = {};
    if (_token) headers['x-admin-token'] = _token;
    let response;
    try {
        response = await fetch(`${API_URL}${endpoint}`, { method: 'GET', headers });
    } catch (_) {
        return { ok: false, error: 'NETWORK_ERROR', mensaje: 'Sin conexión al servidor' };
    }
    if (response.status === 401) {
        let body = null;
        try {
            body = await response.json();
        } catch (_) {
            /* body vacío → expiración */
        }
        return _manejarRechazoAuth(body);
    }
    let data;
    try {
        data = await response.json();
    } catch (_) {
        return { ok: false, error: 'PARSE_ERROR', mensaje: 'Respuesta inválida del servidor' };
    }
    if (!response.ok) {
        const mensaje = (data && (data.mensaje || data.message || data.error)) || 'Error del servidor';
        return { ok: false, error: (data && data.error) || 'HTTP_ERROR', mensaje, httpStatus: response.status };
    }
    return data;
}

// POST autenticado (solo para /mi-config-marketing — autoservicio de tags).
async function post(endpoint, body) {
    const headers = { 'Content-Type': 'application/json' };
    if (_token) headers['x-admin-token'] = _token;
    let response;
    try {
        response = await fetch(`${API_URL}${endpoint}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body || {}),
        });
    } catch (_) {
        return { ok: false, error: 'NETWORK_ERROR', mensaje: 'Sin conexión al servidor' };
    }
    if (response.status === 401) {
        let body = null;
        try {
            body = await response.json();
        } catch (_) {
            /* body vacío → expiración */
        }
        return _manejarRechazoAuth(body);
    }
    let data;
    try {
        data = await response.json();
    } catch (_) {
        return { ok: false, error: 'PARSE_ERROR', mensaje: 'Respuesta inválida del servidor' };
    }
    if (!response.ok) {
        const mensaje = (data && (data.mensaje || data.message || data.error)) || 'Error del servidor';
        return { ok: false, error: (data && data.error) || 'HTTP_ERROR', mensaje, httpStatus: response.status };
    }
    return data;
}

export const ApiClient = {
    canjearTokenAcceso,
    leerTokenValidoDeLaUrl,
    hasDispositivoToken,
    getUltimoEmail,
    login,
    logout,
    isLoggedIn,
    getUser,
    get,
    post,
    setOnSessionExpired,
    activacionEstado,
    activacionSolicitarCodigo,
    activacionCrearContrasena,
    cambiarContrasena,
    resetSolicitarCodigo,
    resetRestablecerContrasena,
    correoSolicitarCodigo,
    correoVerificarCodigo,
};

export default ApiClient;
